import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Divider,
  Link
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Speed as SpeedIcon,
  Analytics as AnalyticsIcon,
  Share as ShareIcon,
  SmartToy as AIIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import Screenshot from './Screenshot';
import ScreenshotGallery from './ScreenshotGallery';
import surveyeditor from '../../assets/images/surveyeditor.webp';
import form from '../../assets/images/form.webp';
import analytics from '../../assets/images/analytics.webp';
import aisurvey from '../../assets/images/aisurvey.webp';
import Dashboard from '../../assets/images/Dashboard.webp';
const WhatIsSurveySlack = () => {
  const steps = [
    {
      title: 'Navigate to the Sign-Up Page',
      desc: 'Open your web browser and go to the SurveySlack website. Click the "Sign Up" button, typically located in the top right corner.',
      img: null
    },
    {
      title: 'Choose Your Sign-Up Method',
      // desc: 'Sign up using your Google account or with your email and password.',
      desc: (
         <>
          <Link href="https://surveyslack.com/app/signup" target="_blank" rel="noopener noreferrer">
            Sign up
          </Link>{' '}
          using your Google account or with your email and password.
        </>
      ),
      img: null,
      tip: 'Using a single sign-on (SSO) method like Google can speed up future logins.'
    },
    {
      title: 'Complete the Registration Form',
      desc: 'If using email, enter your name, email address, and a secure password.',
      img: null
    },
    {
      title: 'Onboarding Questions',
      desc: 'After registration you will see a short onboarding form asking a few quick questions (how you heard about SurveySlack, your role, primary goal, and which features you want to try). You can complete these or skip.',
      img: null
    },
    {
      title: 'Choose Your Plan',
      desc: 'Clicking "Next" (or "Skip") on onboarding takes you to the pricing page. Select Free, Premium, or Corporate — Premium offers a 90-day trial with advanced features.',
      img: null
    },
    {
      title: 'Access the Dashboard',
      desc: 'Once setup is complete, you will be redirected to your main SurveySlack Dashboard.',
      img: null
    }
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        Getting Started: Sign Up and Dashboard Overview
      </Typography>
      <Box sx={{ width: 40, height: 4, bgcolor: 'primary.main', borderRadius: 2, mb: 3 }} />

      {/* Creating Your Account */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Creating Your Account</Typography>

      <Card variant="outlined" sx={{ mb: 3, backgroundColor: (theme) => theme.palette.primary.lighter || 'rgba(25,118,210,0.06)' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>Estimated Time:</Typography>
          <Typography variant="body2" color="text.secondary">5 minutes to create account and explore the dashboard</Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {steps.map((s, idx) => (
          <Grid item xs={12} key={idx}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>{idx + 1}</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{s.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{s.desc}</Typography>
                
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Dashboard Overview */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Dashboard Overview</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>The Dashboard is your central hub for managing all your surveys.</Typography>

      {/* <Box sx={{ mb: 3, p: 2, border: '1px solid',  borderRadius: 1, bgcolor: 'background.paper' }}> */}
      
         <Box
          component="img"
          src={Dashboard}
          alt="Main Dashboard view with callouts for key sections"
          sx={{
            width: '100%',
            borderRadius: 1,
            display: 'block',
            boxShadow: (theme) => theme.shadows[3],
            mb: 3, 
          }}
        />
      {/* </Box> */}

      <TableContainer component={Paper} variant="outlined" sx={{ borderColor: 'divider' }}>
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
        <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
        <TableCell sx={{ fontWeight: 700 }}>What You Can Do</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        <TableCell>Welcome Header</TableCell>
        <TableCell>Greet the user and set context for the dashboard.</TableCell>
        <TableCell>View account name and quick access actions.</TableCell>
      </TableRow>

      <TableRow>
        <TableCell>Quick Stats Cards</TableCell>
        <TableCell>Show a high-level overview of survey performance.</TableCell>
        <TableCell>
          View total surveys, active surveys, total responses, and average response rate.
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell>Primary Action Buttons</TableCell>
        <TableCell>Enable users to quickly start or explore surveys.</TableCell>
        <TableCell>
          Create a new survey, browse templates, or generate surveys using AI.
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell>Subscription Status Banner</TableCell>
        <TableCell>Display current plan and subscription details.</TableCell>
        <TableCell>
          View plan type, start date, expiry date, and remaining days.
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell>Recent Surveys List</TableCell>
        <TableCell>Manage recently created surveys.</TableCell>
        <TableCell>
          Edit, share, view results, clone, or delete surveys and check their status.
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell>User & Support Actions</TableCell>
        <TableCell>Access help resources and account options.</TableCell>
        <TableCell>
          Open documentation, report issues, or manage account settings.
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
</TableContainer>


      {/* CTA */}
      <Box sx={{ py: 4 }}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Next Steps</Typography>
            <Typography variant="body1" color="text.secondary">
              Now that your account is set up, you're ready to create your first survey! Head to the next guide to learn about the three ways to create a survey.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                size="medium"
                href="/app/documentations/building-surveys/creating-survey"
              >
                Next
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default WhatIsSurveySlack;